import React, { useMemo, useState } from 'react';
import { useCustomerDetails } from '../hook';
import {
  CfaTabContent,
  ContactsTabContent,
  DetailPickerModal,
  DetailsTabContent,
  useCompanyGroupPicker,
} from './components';
import './customer-company-details-content.scss';
import { CustomerCompanyDetailsShimmer } from './components/customer-company-details-shimmer';
import {
  dispatch,
  ErrorVariants,
  getWorkspaceId,
  showToast,
  useAddGroupMemberMutation,
  useAppSelector,
  useMoveGroupMembersMutation,
} from '@zonofi/zono-money-store';

type CompanyDetailsTab = 'details' | 'cfa' | 'contacts';

type CustomerCompanyDetailsContentProps = {
  tabsBackgroundColor?: string;
};

export const CustomerCompanyDetailsContent: React.FC<
  CustomerCompanyDetailsContentProps
> = ({ tabsBackgroundColor = '#132c45' }) => {
  const { customerData, isLoading, customerId, refreshProfile } =
    useCustomerDetails();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const [activeTab, setActiveTab] = useState<CompanyDetailsTab>('details');
  const picker = useCompanyGroupPicker();
  const [addGroupMember] = useAddGroupMemberMutation();
  const [moveGroupMembers] = useMoveGroupMembersMutation();

  const cfaRows = useMemo(() => customerData?.cfa ?? [], [customerData?.cfa]);
  const contactRows = useMemo(
    () => customerData?.contacts ?? [],
    [customerData?.contacts]
  );
  const lifeCycleRows = useMemo(
    () => (customerData as any)?.customerLifeCycleGroups ?? [],
    [customerData]
  );
  const segmentRows = useMemo(
    () =>
      (customerData as any)?.segments ??
      (customerData as any)?.customer_segment ??
      [],
    [customerData]
  );
  const routeRows = useMemo(
    () => (customerData as any)?.routes ?? [],
    [customerData]
  );
  const customFieldRows = useMemo(() => {
    const attributeValues = ((customerData as any)?.attributeValues ??
      {}) as Record<string, unknown>;
    return Object.entries(attributeValues);
  }, [customerData]);

  React.useEffect(() => {
    picker.setSelectedIds((prev) => ({
      lifecycle: prev.lifecycle.length
        ? prev.lifecycle
        : lifeCycleRows
            .map((v: any) => v?.id)
            .filter(Boolean)
            .slice(0, 1),
      segments: prev.segments.length
        ? prev.segments
        : segmentRows.map((v: any) => v?.id).filter(Boolean),
      routes: prev.routes.length
        ? prev.routes
        : routeRows.map((v: any) => v?.id).filter(Boolean),
    }));
  }, [lifeCycleRows, segmentRows, routeRows]);

  const fallbackLifeCycleLabels = useMemo(
    () =>
      lifeCycleRows
        .map((item: any) => `${item?.emojiId ?? ''} ${item?.name ?? ''}`.trim())
        .filter(Boolean),
    [lifeCycleRows]
  );
  const fallbackSegmentLabels = useMemo(
    () =>
      segmentRows
        .map((item: any) =>
          String(item?.name ?? item?.channelName ?? '').trim()
        )
        .filter(Boolean),
    [segmentRows]
  );
  const fallbackRouteLabels = useMemo(
    () =>
      routeRows
        .map((item: any) =>
          String(item?.name ?? item?.channelName ?? '').trim()
        )
        .filter(Boolean),
    [routeRows]
  );

  return (
    <div
      className="company-details-screen"
      style={
        {
          '--company-details-tabs-bg': tabsBackgroundColor,
        } as React.CSSProperties
      }
    >
      <div className="company-details-screen__tabs">
        {[
          { id: 'details', label: 'Details' },
          { id: 'cfa', label: 'CFA/Divisions' },
          { id: 'contacts', label: 'Customer Contacts' },
        ].map((tab) => (
          <button
            type="button"
            key={tab.id}
            onClick={() => setActiveTab(tab.id as CompanyDetailsTab)}
            className={`company-details-screen__tab-btn font-regular ${activeTab === tab.id ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {isLoading ? (
        <div className="company-details-screen__loading font-regular">
          {/* Loading... */}
          <CustomerCompanyDetailsShimmer />
        </div>
      ) : activeTab === 'details' ? (
        <DetailsTabContent
          customerData={customerData}
          selectedLifeCycle={
            picker.selectedLabels('lifecycle').length
              ? picker.selectedLabels('lifecycle')
              : fallbackLifeCycleLabels
          }
          selectedSegments={
            picker.selectedLabels('segments').length
              ? picker.selectedLabels('segments')
              : fallbackSegmentLabels
          }
          selectedRoutes={
            picker.selectedLabels('routes').length
              ? picker.selectedLabels('routes')
              : fallbackRouteLabels
          }
          customFieldRows={customFieldRows}
          onOpenPicker={picker.openPicker}
        />
      ) : activeTab === 'cfa' ? (
        <CfaTabContent cfaRows={cfaRows} />
      ) : (
        <ContactsTabContent contactRows={contactRows} />
      )}
      {picker.activePicker && (
        <DetailPickerModal
          isVisible
          type={picker.activePicker}
          groupData={picker.groupMap[picker.activePicker]}
          selectedValues={picker.selectedIds[picker.activePicker]}
          searchText={picker.searchText}
          setSearchText={picker.setSearchText}
          onClose={() => {
            picker.setActivePicker(null);
            picker.setSearchText('');
          }}
          onSubmit={async (nextSelection) => {
            const type = picker.activePicker!;
            const trimmedSelection =
              type === 'lifecycle' || type === 'routes'
                ? nextSelection.slice(0, 1)
                : nextSelection;

            if (
              type === 'routes' &&
              sellerWorkspaceId &&
              customerId &&
              trimmedSelection.length
            ) {
              try {
                const targetRouteId = trimmedSelection[0];
                const currentRouteIds = routeRows
                  .map((v: any) => v?.id)
                  .filter(Boolean);

                if (
                  currentRouteIds.length &&
                  currentRouteIds[0] !== targetRouteId
                ) {
                  await moveGroupMembers({
                    sellerWorkspaceId,
                    body: {
                      fromGroupId: currentRouteIds[0],
                      toGroupId: targetRouteId,
                      customerIds: [customerId],
                    },
                  }).unwrap();
                } else if (!currentRouteIds.length) {
                  await addGroupMember({
                    sellerWorkspaceId,
                    groupId: targetRouteId,
                    body: { customerIds: [customerId] },
                  }).unwrap();
                }

                refreshProfile?.();
                dispatch(
                  showToast({
                    message: 'Route assigned successfully',
                    type: ErrorVariants.SUCCESS,
                    canDisplay: true,
                    responseStatus: 'Success',
                  })
                );
              } catch (error) {
                dispatch(
                  showToast({
                    message: 'Failed to assign route',
                    type: ErrorVariants.DANGER,
                    canDisplay: true,
                    responseStatus: 'Error',
                  })
                );
                return;
              }
            }

            picker.setSelectedIds((prev) => ({
              ...prev,
              [type]:
                type === 'lifecycle' || type === 'routes'
                  ? trimmedSelection
                  : nextSelection,
            }));
            picker.setActivePicker(null);
            picker.setSearchText('');
          }}
        />
      )}
    </div>
  );
};
